import { useRef } from 'react';
import { Decrement, Increment } from '@sas/react-svg';
import cx from 'classnames';
import { createUseTextLocalizer } from '../../../locale/createUseTextLocalizer';
import { postfixTestId } from '../../../util/testId';
import { ButtonVariant } from '../../Button';
import { BaseButton } from '../../Button/__internal__';
import bundle from '../NovaComponents-NumericStepper-gui-icu.properties';
import cn from '../NumericStepper.css.json';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const useTextLocalizer = createUseTextLocalizer(bundle);
export const SpinButtons = ({
  children,
  className,
  disabled,
  minimum,
  maximum,
  stepSize = 1,
  pageSize = stepSize * 2,
  style,
  testId,
  tooltip,
  value,
  onValueChange,
  onValueCommit
}) => {
  const localize = useTextLocalizer();
  const preValueRef = useRef(null);
  const mouseClicking = useRef(false);
  const rootElementRef = useRef(null);
  const fireValueChangeEvent = value => {
    if (value !== undefined) {
      onValueChange?.({
        newValue: value
      });
    }
  };
  const fireValueCommitEvent = (value, reason) => {
    onValueCommit?.({
      value,
      reason
    });
  };

  // This is requested in NOVACOMMONS-4457
  const onBlur = e => {
    if (!mouseClicking.current && !rootElementRef.current?.contains(e.relatedTarget)) {
      fireValueCommitEvent(undefined, 'blur');
    }
  };
  const upButtonDisabled = disabled || value !== undefined && value >= maximum;
  const downButtonDisabled = disabled || value !== undefined && value <= minimum;
  const handleMousePress = increasing => {
    if (disabled) return;
    const nextValue = getNextValue(value, increasing, stepSize, minimum, maximum);
    preValueRef.current = value;
    mouseClicking.current = true;
    fireValueChangeEvent(nextValue);
  };
  const commitValue = reason => {
    if (preValueRef.current !== null && preValueRef.current !== value && value !== undefined) {
      preValueRef.current = null;
      fireValueCommitEvent(value, reason);
    }
  };
  const handleMouseRelease = e => {
    if (e.button === 0) {
      e.preventDefault();
      mouseClicking.current = false;
      commitValue('click');
    }
  };
  const handleButtonKeyUp = e => {
    if (disabled) return;
    switch (e.key) {
      case 'Enter':
      case ' ':
        e.preventDefault();
        commitValue('keyin');
        break;
      default:
        break;
    }
  };
  const handleContainerKeyUp = e => {
    if (disabled) return;
    switch (e.key) {
      case 'ArrowDown':
      case 'ArrowUp':
      case 'PageDown':
      case 'PageUp':
      case 'Home':
      case 'End':
        e.preventDefault();
        commitValue('keyin');
        break;
      default:
        break;
    }
  };
  const handleContainerKeyDown = e => {
    if (disabled) return;
    let nextValue;
    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        if (downButtonDisabled) return;
        nextValue = getNextValue(value, false, stepSize, minimum, maximum);
        break;
      case 'ArrowUp':
        e.preventDefault();
        if (upButtonDisabled) return;
        nextValue = getNextValue(value, true, stepSize, minimum, maximum);
        break;
      case 'PageDown':
        e.preventDefault();
        if (downButtonDisabled) return;
        nextValue = getNextValue(value, false, pageSize, minimum, maximum);
        break;
      case 'PageUp':
        e.preventDefault();
        if (upButtonDisabled) return;
        nextValue = getNextValue(value, true, pageSize, minimum, maximum);
        break;
      case 'Home':
        e.preventDefault();
        if (value === minimum) return;
        nextValue = minimum;
        break;
      case 'End':
        e.preventDefault();
        if (value === maximum) return;
        nextValue = maximum;
        break;
      default:
        break;
    }
    if (nextValue !== undefined) {
      preValueRef.current = value;
      fireValueChangeEvent(nextValue);
    }
  };
  return /*#__PURE__*/_jsxs("div", {
    ref: rootElementRef,
    className: cx(cn.container, className),
    style: style,
    title: tooltip,
    "data-testid": testId,
    onKeyDown: handleContainerKeyDown,
    onKeyUp: handleContainerKeyUp,
    children: [children, /*#__PURE__*/_jsx(BaseButton, {
      icon: {
        icon: Decrement,
        tooltip: ''
      },
      className: cx(cn.button, cn.downButton),
      onPress: () => {
        handleMousePress(false);
      },
      onKeyUp: handleButtonKeyUp,
      onMouseUp: handleMouseRelease,
      onMouseLeave: handleMouseRelease,
      focusable: false,
      onBlur: onBlur,
      disabled: downButtonDisabled,
      variant: ButtonVariant.Icon,
      ariaLabel: localize('decrementButton.aria'),
      testId: postfixTestId(testId, 'decrementButton'),
      enableContinuousPress: true
    }), /*#__PURE__*/_jsx(BaseButton, {
      icon: {
        icon: Increment,
        tooltip: ''
      },
      className: cx(cn.button, cn.upButton),
      onPress: () => {
        handleMousePress(true);
      },
      onKeyUp: handleButtonKeyUp,
      onMouseUp: handleMouseRelease,
      onMouseLeave: handleMouseRelease,
      onBlur: onBlur,
      focusable: false,
      disabled: upButtonDisabled,
      variant: ButtonVariant.Icon,
      ariaLabel: localize('incrementButton.aria'),
      testId: postfixTestId(testId, 'incrementButton'),
      enableContinuousPress: true
    })]
  });
};
function doAccuratePlus(a, b) {
  const digitsA = convertScientificNumber(a).split('.'),
    digitsB = convertScientificNumber(b).split('.');
  if (digitsA.length === 1 && digitsB.length === 1) {
    // integer
    return Number.parseInt(a.toString()) + b;
  }
  const times = Math.pow(10, Math.max(digitsA[1] ? digitsA[1].length : 0, digitsB[1] ? digitsB[1].length : 0));
  a = Math.round(a * times);
  b = Math.round(b * times);
  return (a + b) / times;
}
function getNextValue(value, increasing, stepSize, min, max) {
  let newValue;
  if (increasing) {
    if (value === undefined) {
      newValue = max;
    } else if (value < min) {
      newValue = min;
    } else {
      newValue = doAccuratePlus(value, stepSize);
    }
    if (newValue > max) {
      newValue = max;
    }
  } else {
    if (value === undefined) {
      newValue = min;
    } else if (value > max) {
      newValue = max;
    } else {
      newValue = doAccuratePlus(value, -stepSize);
    }
    if (newValue < min) {
      newValue = min;
    }
  }
  return newValue;
}
function convertScientificNumber(value) {
  const scientificNotationRegex = /^([+-]?\d+(\.\d+)?)[eE][+-]?(\d+)$/;
  const match = value.toString().match(scientificNotationRegex);
  if (!match) {
    return value.toString();
  }
  const decimalInBase = match[2] ? match[2].length - 1 : 0;
  const exponent = parseInt(match[3], 10);
  return value.toFixed(exponent + decimalInBase);
}
//# sourceMappingURL=SpinButtons.js.map